import {
  MouseEvent as ReactMouseEvent,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Box,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  useTheme,
} from '@mui/material'
import { EmptyState } from '@ui2/feedback'
import { MaterialSymbol } from '@ui2/icons'
import { useAppStore } from '@stores'
import { eventMatchesPointerModifier } from 'src/shortcuts/pointerModifiers'
import { useResults2FokusEffect } from '../../hooks/useResults2FokusEffect'
import type { Results2ListItem } from '../Results2ElementList'
import Results2ListPaneActionsFooter from '../Results2ListPaneActionsFooter'
import AssemblyFilterButton from './AssemblyFilterButton'

type Props = {
  items: Results2ListItem[]
  selectedId?: string
  selectedIds?: string[]
  onSelect?: (id: string) => boolean | void
  onSelectionChange?: (ids: string[]) => boolean | void
  isLoading?: boolean
  emptyMessage?: string
  onCalculate?: () => void
  /** Cancels in-flight results loading; surfaced as an "Abbrechen" footer button. */
  onAbort?: () => void
  /** When true, shows the "Abbrechen" button next to "Berechnen". */
  showAbort?: boolean
  fokusCamera: boolean
  onFokusCameraChange: (next: boolean) => void
  fokusTransparency: boolean
  onFokusTransparencyChange: (next: boolean) => void
  disableForm?: boolean
}

/**
 * Simple list variant for "assembly-like" elements where grouping/export/utilization isn't relevant
 * (e.g. Wände, Decken, Dächer).
 */
const Results2AssemblyList = ({
  items,
  selectedId,
  selectedIds,
  onSelect,
  onSelectionChange,
  isLoading = false,
  emptyMessage = 'Keine Daten verfügbar',
  onCalculate,
  onAbort,
  showAbort = false,
  fokusCamera,
  onFokusCameraChange,
  fokusTransparency,
  onFokusTransparencyChange,
  disableForm = false,
}: Props): ReactElement => {
  const theme = useTheme()
  const filter3d = useAppStore(state => state.results2Filter3d)
  const setFilter3d = useAppStore(state => state.setResults2Filter3d)
  const listRef = useRef<HTMLUListElement | null>(null)
  const [filter, setFilter] = useState('')
  const [selectedAssemblies, setSelectedAssemblies] = useState<string[]>([])

  // Distinct assembly labels (the rows' `qs`) available to filter by.
  const assemblyOptions = useMemo(() => {
    const set = new Set<string>()
    for (const item of items) {
      const qs = item.qs?.trim()
      if (qs) set.add(qs)
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [items])

  // Drop selections that aren't in the current option set (e.g. after switching between the
  // assembly tabs, where this component is reused) so a stale selection can't hide every row.
  const effectiveSelectedAssemblies = useMemo(
    () => selectedAssemblies.filter(assembly => assemblyOptions.includes(assembly)),
    [selectedAssemblies, assemblyOptions],
  )

  useResults2FokusEffect(fokusTransparency, selectedId, selectedIds)

  const selectedIdSet = useMemo(
    () => new Set(selectedIds ?? (selectedId ? [selectedId] : [])),
    [selectedId, selectedIds],
  )
  const lastSelectionIdRef = useRef<string | null>(selectedId ?? null)

  const effectiveSelectedId = useMemo(() => {
    if (selectedId) return selectedId
    return selectedIds?.[0]
  }, [selectedId, selectedIds])

  const visibleItems = useMemo(() => {
    const q = filter.trim().toLowerCase()
    return items.filter(item => {
      // Normalize `qs` the same way assemblyOptions does, so trimmed options match their rows.
      const qs = item.qs?.trim() ?? ''
      if (effectiveSelectedAssemblies.length > 0 && !effectiveSelectedAssemblies.includes(qs))
        return false
      if (!q) return true
      return (
        item.id.toLowerCase().includes(q) ||
        (item.label ?? '').toLowerCase().includes(q) ||
        qs.toLowerCase().includes(q)
      )
    })
  }, [filter, items, effectiveSelectedAssemblies])

  // Keep selected element visible when selection comes from 3D scene.
  useEffect(() => {
    if (!effectiveSelectedId) return
    const listEl = listRef.current
    if (!listEl) return

    let cancelled = false
    let attempts = 0
    let raf = 0

    const tick = () => {
      if (cancelled) return
      attempts += 1

      const rowEl = listEl.querySelector<HTMLElement>(`[data-row-id="${effectiveSelectedId}"]`)
      if (!rowEl) {
        if (attempts < 8) raf = window.requestAnimationFrame(tick)
        return
      }

      const rowRect = rowEl.getBoundingClientRect()
      const containerRect = listEl.getBoundingClientRect()
      const fullyVisible =
        rowRect.top >= containerRect.top && rowRect.bottom <= containerRect.bottom
      if (!fullyVisible) rowEl.scrollIntoView({ block: 'nearest' })
    }

    raf = window.requestAnimationFrame(tick)
    return () => {
      cancelled = true
      window.cancelAnimationFrame(raf)
    }
  }, [effectiveSelectedId, items])

  const handleRowClick = useCallback(
    (id: string, event: ReactMouseEvent<HTMLElement>) => {
      const hasListModifier =
        eventMatchesPointerModifier(event, 'listToggle') ||
        eventMatchesPointerModifier(event, 'multiSelect')
      // No multiselect available without the explicit handler.
      if (!onSelectionChange || !hasListModifier) {
        let ok = true
        if (onSelectionChange) ok = onSelectionChange([id]) !== false
        else if (onSelect) ok = onSelect(id) !== false
        if (!ok) return
        lastSelectionIdRef.current = id
        return
      }

      const renderedIds = visibleItems.map(i => i.id)
      const currentSelectedIds = selectedIds ?? (selectedId ? [selectedId] : [])
      let nextSelectedIds: string[]

      if (eventMatchesPointerModifier(event, 'multiSelect') && lastSelectionIdRef.current) {
        const startIndex = renderedIds.indexOf(lastSelectionIdRef.current)
        const endIndex = renderedIds.indexOf(id)
        if (startIndex !== -1 && endIndex !== -1) {
          const [from, to] = startIndex < endIndex ? [startIndex, endIndex] : [endIndex, startIndex]
          nextSelectedIds = Array.from(
            new Set([...currentSelectedIds, ...renderedIds.slice(from, to + 1)]),
          )
        } else {
          nextSelectedIds = Array.from(new Set([...currentSelectedIds, id]))
        }
      } else if (selectedIdSet.has(id)) {
        nextSelectedIds = currentSelectedIds.filter(selected => selected !== id)
      } else {
        nextSelectedIds = [...currentSelectedIds, id]
      }

      if (onSelectionChange(nextSelectedIds) === false) return

      lastSelectionIdRef.current = id
    },
    [onSelect, onSelectionChange, selectedId, selectedIdSet, selectedIds, visibleItems],
  )

  const listBody = isLoading ? (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CircularProgress size={18} />
    </Box>
  ) : visibleItems.length === 0 ? (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <EmptyState sx={{ fontSize: 13 }}>
        {filter.trim() || effectiveSelectedAssemblies.length > 0 ? 'Keine Treffer' : emptyMessage}
      </EmptyState>
    </Box>
  ) : (
    <List ref={listRef} dense disablePadding sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
      {visibleItems.map(item => {
        const selected = selectedIdSet.has(item.id)
        return (
          <ListItemButton
            key={item.id}
            data-row-id={item.id}
            data-cy="results2-list-row-select"
            selected={selected}
            onClick={event => handleRowClick(item.id, event)}
            sx={{
              py: 0.2,
              px: 0.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
              borderLeft: `3px solid ${selected ? theme.palette.primary.main : 'transparent'}`,
              background: selected ? 'rgba(55,132,52,0.08)' : 'transparent',
              transition: 'background 120ms',
              '&:hover': {
                background: selected ? 'rgba(55,132,52,0.08)' : theme.palette.action.hover,
              },
            }}
          >
            <ListItemText
              primary={<span data-cy="results2-list-row-label">{item.label ?? item.id}</span>}
              secondary={
                <Stack direction="row" spacing={1} sx={{ opacity: 0.85 }}>
                  <span>{item.qs || 'n/a'}</span>
                </Stack>
              }
              primaryTypographyProps={{ sx: { fontSize: 13, fontWeight: 500 } }}
              secondaryTypographyProps={{ sx: { fontSize: 12 } }}
            />
          </ListItemButton>
        )
      })}
    </List>
  )

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <AssemblyFilterButton
          options={assemblyOptions}
          selected={effectiveSelectedAssemblies}
          onChange={setSelectedAssemblies}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            size="small"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Suchen…"
            fullWidth
            InputProps={{
              startAdornment: (
                <Box sx={{ display: 'inline-flex', mr: 0.75, color: theme.palette.text.secondary }}>
                  <MaterialSymbol icon="search" size={16} />
                </Box>
              ),
              sx: { fontSize: 13, height: 30 },
            }}
          />
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{listBody}</Box>
      <Results2ListPaneActionsFooter
        filter3d={filter3d}
        onFilter3dChange={setFilter3d}
        fokusCamera={fokusCamera}
        onFokusCameraChange={onFokusCameraChange}
        fokusTransparency={fokusTransparency}
        onFokusTransparencyChange={onFokusTransparencyChange}
        onThreeDTargetBauteile={undefined}
        onThreeDTargetWaende={undefined}
        showBlockpadButton={false}
        onCalculate={onCalculate}
        onAbort={onAbort}
        showAbort={showAbort}
        isLoading={isLoading}
        disableForm={disableForm}
      />
    </Box>
  )
}

export default Results2AssemblyList
